import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PROSPECT_STATUSES, COURSES, courseById, type ProspectStatus } from "@mizpah/shared";
import { api, AdminError } from "../adminClient.js";

interface Lead {
  prospectId: string; fullName: string; email: string; courseId: string;
  preferredBatchMonth?: string; status: ProspectStatus; emailStatus?: string; createdAt: string;
}

type StatusFilter = ProspectStatus | "All";
type DateKey = "all" | "10d" | "20d" | "30d" | "month" | "lastmonth";

const DATE_FILTERS: { key: DateKey; label: string }[] = [
  { key: "10d", label: "Last 10 days" },
  { key: "20d", label: "Last 20 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "month", label: "This month" },
  { key: "lastmonth", label: "Last month" },
  { key: "all", label: "All time" },
];

// Status pills: actionable statuses first, "All" last (per owner request).
const STATUS_PILLS: StatusFilter[] = [...PROSPECT_STATUSES, "All"];

const StatusBadge = ({ s }: { s: ProspectStatus }) => <span className={`badge b-${s}`}>{s}</span>;
const ago = (iso: string) => { const d = (Date.now() - new Date(iso).getTime()) / 36e5; return d < 1 ? "just now" : d < 24 ? `${Math.floor(d)}h ago` : `${Math.floor(d / 24)}d ago`; };

function inRange(iso: string, key: DateKey): boolean {
  if (key === "all") return true;
  const t = new Date(iso).getTime();
  const now = new Date();
  if (key === "10d") return t >= now.getTime() - 10 * 864e5;
  if (key === "20d") return t >= now.getTime() - 20 * 864e5;
  if (key === "30d") return t >= now.getTime() - 30 * 864e5;
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  if (key === "month") return t >= thisMonth;
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
  return t >= lastMonth && t < thisMonth; // lastmonth
}

export default function InboxPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<Lead[]>([]);
  const [filter, setFilter] = useState<StatusFilter>("New"); // default to new, actionable leads
  const [dateKey, setDateKey] = useState<DateKey>("30d"); // default to recent activity
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api<{ items: Lead[] }>("/admin/inquiries")
      .then((d) => setItems(d.items))
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load inquiries"))
      .finally(() => setLoading(false));
  }, []);

  // Apply period + course + search first; the status pill counts and the list
  // all reflect that base set.
  const base = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) =>
      inRange(i.createdAt, dateKey) &&
      (courseFilter === "all" || i.courseId === courseFilter) &&
      (!needle || i.fullName.toLowerCase().includes(needle))
    );
  }, [items, dateKey, courseFilter, q]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { All: base.length };
    for (const s of PROSPECT_STATUSES) c[s] = base.filter((i) => i.status === s).length;
    return c;
  }, [base]);

  const shown = filter === "All" ? base : base.filter((i) => i.status === filter);

  // Cold leads are auto-aged after 30 days, so they're always older than the
  // default "Last 30 days" window. Selecting the Cold pill widens the period to
  // "All time" so they're actually visible (otherwise the list/count read 0).
  const selectStatus = (s: StatusFilter) => {
    setFilter(s);
    if (s === "Cold" && dateKey !== "all") setDateKey("all");
  };

  return (
    <>
      <h1>Inquiries</h1>
      <p className="subhead">New leads from the contact form. Click one to follow up.</p>

      <div className="filters">
        <div className="searchbox">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <input type="search" placeholder="Search by name..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="filter-row">
          <div className="pills">
            {STATUS_PILLS.map((s) => (
              <button key={s} className={`pill${filter === s ? " on" : ""}`} onClick={() => selectStatus(s)}>
                {s} <span className="c">{counts[s] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="selects">
            <select className="filter-select" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} aria-label="Filter by course">
              <option value="all">All courses</option>
              {COURSES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="filter-select" value={dateKey} onChange={(e) => setDateKey(e.target.value as DateKey)} aria-label="Filter by period">
              {DATE_FILTERS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && <p className="subhead">Loading...</p>}
      {error && <div className="login-err">{error}</div>}
      {!loading && !error && shown.length === 0 && (
        <div className="card empty">No inquiries match your filters.</div>
      )}

      {shown.length > 0 && (
        <>
          <table>
            <thead><tr><th>Name</th><th>Course</th><th>Start</th><th>Status</th><th>Received</th></tr></thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.prospectId} onClick={() => nav(`/admin/inquiries/${i.prospectId}`)}>
                  <td>
                    <div className="nm">{i.fullName}</div>
                    <div className="sub">{i.email}{(i.emailStatus === "bounced" || i.emailStatus === "complained") && <span className="flag" style={{ marginLeft: 6 }}>email {i.emailStatus}</span>}</div>
                  </td>
                  <td>{courseById(i.courseId)?.name ?? i.courseId}</td>
                  <td>{i.preferredBatchMonth ?? "-"}</td>
                  <td><StatusBadge s={i.status} /></td>
                  <td className="sub">{ago(i.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="cards-list">
            {shown.map((i) => (
              <div key={i.prospectId} className="card pad" style={{ marginBottom: 12, cursor: "pointer" }} onClick={() => nav(`/admin/inquiries/${i.prospectId}`)}>
                <div className="row"><div className="nm">{i.fullName}</div><StatusBadge s={i.status} /></div>
                <div className="sub" style={{ marginTop: 4 }}>{courseById(i.courseId)?.name} · {i.preferredBatchMonth} · {ago(i.createdAt)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
